import { text2Bold } from '@ardoq/typography';
import { colors } from '@ardoq/design-tokens';
import styled from 'styled-components';
import { FlexBox, Stack } from '@ardoq/layout';
import { type I18nLocale, t } from '@ardoq/i18n';
import { MatrixCell } from './MatrixCell';

type CellData = Record<string, string[]>;

export type PreviewMatrixProps = {
  i18nLocale: I18nLocale;
  totals: {
    updated: Record<string, number>;
    created: Record<string, number>;
    deleted?: Record<string, number>;
  };
  deleteMatrixEnabled?: boolean;
  transferDirection: 'import' | 'export';
  preview?: {
    created: CellData;
    updated: CellData;
    deleted: CellData;
  };
  entityTypes?: string[];
  toCreateLabel?: string;
  toUpdateLabel?: string;
  toDeleteLabel?: string;
};

const defaultEntityTypes = {
  import: ['workspaces', 'components', 'references', 'fields', 'tags'],
  export: ['tables', 'rows'],
};

export const PreviewMatrix = ({
  i18nLocale,
  totals,
  preview,
  transferDirection,
  deleteMatrixEnabled,
  entityTypes = defaultEntityTypes[transferDirection],
  toCreateLabel = t(
    i18nLocale,
    'integrations:previewMatrix.toCreate',
    'to create'
  ),
  toUpdateLabel = t(
    i18nLocale,
    'integrations:previewMatrix.toUpdate',
    'to update'
  ),
  toDeleteLabel = t(
    i18nLocale,
    'integrations:previewMatrix.toDelete',
    'to delete'
  ),
}: PreviewMatrixProps) => {
  return (
    <FlexBox gap="xsmall" justify="center" flexWrap>
      {entityTypes.map(c => {
        return (
          <MatrixColumn
            i18nLocale={i18nLocale}
            key={c}
            title={c}
            created={totals.created[c] || 0}
            createdPreview={preview?.created[c] || []}
            updated={totals.updated[c] || 0}
            updatedPreview={preview?.updated[c] || []}
            deleted={totals.deleted?.[c] || 0}
            deletedPreview={preview?.deleted[c] || []}
            deleteMatrixEnabled={deleteMatrixEnabled}
            toCreateLabel={toCreateLabel}
            toUpdateLabel={toUpdateLabel}
            toDeleteLabel={toDeleteLabel}
          />
        );
      })}
    </FlexBox>
  );
};

type MatrixColumnProps = {
  i18nLocale: I18nLocale;
  title: string;
  updated: number;
  updatedPreview: string[];
  created: number;
  createdPreview: string[];
  deleted: number;
  deletedPreview: string[];
  deleteMatrixEnabled?: boolean;
  toCreateLabel: string;
  toUpdateLabel: string;
  toDeleteLabel: string;
};

const MatrixColumn = ({
  i18nLocale,
  title,
  updated,
  updatedPreview,
  created,
  createdPreview,
  deleted,
  deletedPreview,
  deleteMatrixEnabled,
  toCreateLabel,
  toUpdateLabel,
  toDeleteLabel,
}: MatrixColumnProps) => {
  return (
    <Stack align="center" gap="xsmall">
      <ColumnTitle>{title}</ColumnTitle>
      <MatrixCell
        i18nLocale={i18nLocale}
        title={title}
        count={created}
        preview={createdPreview}
        caption={toCreateLabel}
        color={colors.green600}
      />
      <MatrixCell
        i18nLocale={i18nLocale}
        title={title}
        count={updated}
        preview={updatedPreview}
        caption={toUpdateLabel}
        color={colors.blue600}
      />
      {deleteMatrixEnabled && (
        <MatrixCell
          i18nLocale={i18nLocale}
          title={title}
          count={deleted}
          preview={deletedPreview}
          caption={toDeleteLabel}
          color={colors.red700}
        />
      )}
    </Stack>
  );
};

const ColumnTitle = styled.div`
  text-transform: capitalize;
  ${text2Bold}
`;
